import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

import { CropControls } from './upload/CropControls';
import { MediaSelector, MEDIA_TABS } from './upload/MediaSelector';
import { DetailsStep } from './upload/DetailsStep';
import { BatchStep, RecordingStep, CameraStep } from './upload/UploadSteps';
import { useUpload } from '../hooks/useUpload';
import { VibeCelebration } from './VibeCelebration';

function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const rad = (rotation * Math.PI) / 180;
      const sin = Math.abs(Math.sin(rad));
      const cos = Math.abs(Math.cos(rad));
      const rotW = img.width * cos + img.height * sin;
      const rotH = img.width * sin + img.height * cos;
      const rotCanvas = document.createElement('canvas');
      rotCanvas.width = rotW;
      rotCanvas.height = rotH;
      const rotCtx = rotCanvas.getContext('2d');
      rotCtx.translate(rotW / 2, rotH / 2);
      rotCtx.rotate(rad);
      rotCtx.drawImage(img, -img.width / 2, -img.height / 2);
      const safeX = Math.max(0, Math.round(pixelCrop.x));
      const safeY = Math.max(0, Math.round(pixelCrop.y));
      const safeW = Math.round(pixelCrop.width);
      const safeH = Math.round(pixelCrop.height);
      canvas.width = safeW;
      canvas.height = safeH;
      ctx.drawImage(rotCanvas, safeX, safeY, safeW, safeH, 0, 0, safeW, safeH);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.95);
    };
    img.onerror = reject;
    img.src = imageSrc;
  });
}

export const ImageCropUpload = ({ category, onClose, onSuccess }) => {
  const [mediaType, setMediaType] = useState('image');
  const [step, setStep] = useState('select');
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedPreview, setCroppedPreview] = useState(null);
  const [aspect, setAspect] = useState(1);
  const [fileBlob, setFileBlob] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileQueue, setFileQueue] = useState([]);
  const [note, setNote] = useState('');
  const [vibeDate, setVibeDate] = useState('');
  const [cameraStream, setCameraStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [hunterDone, setHunterDone] = useState(false);
  const videoPreviewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Upload API finished — mark upload as done, but DON'T show celebration yet
  const handleUploadDone = useCallback(() => {
    setUploadDone(true);
  }, []);

  // Hunter transition ended (beast reached 100%) — NOW show celebration
  const handleHunterFinished = useCallback(() => {
    setHunterDone(true);
  }, []);

  // Sequential: only fire celebration AFTER both upload AND hunter are done
  useEffect(() => {
    if (uploadDone && hunterDone && !showCelebration) {
      setShowCelebration(true);
    }
  }, [uploadDone, hunterDone, showCelebration]);

  const handleCelebrationComplete = useCallback(() => {
    setShowCelebration(false);
    setUploadDone(false);
    setHunterDone(false);
    onSuccess();
    onClose();
  }, [onSuccess, onClose]);

  const { uploading, uploadPercent, uploadProgress, error, setError, uploadSingle, uploadBatch } = useUpload({
    category,
    onSuccess: handleUploadDone,
    onClose: () => {}, // Don't close immediately — celebration handles it
  });

  useEffect(() => {
    return () => {
      if (cameraStream) cameraStream.getTracks().forEach((t) => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [cameraStream]);

  // Lock body scroll when upload modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const resetState = () => {
    setStep('select');
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setCroppedPreview(null);
    setFileBlob(null);
    setFilePreview(null);
    setFileName('');
    setIsRecording(false);
    setRecordingTime(0);
    setError('');
    setVibeDate('');
    if (cameraStream) { cameraStream.getTracks().forEach((t) => t.stop()); setCameraStream(null); }
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const onFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (mediaType === 'image' && files.length === 1) {
      const reader = new FileReader();
      reader.onloadend = () => { setImageSrc(reader.result); setStep('crop'); };
      reader.readAsDataURL(files[0]);
      setFileName(files[0].name);
    } else if (files.length > 1) {
      setFileQueue(files);
      setStep('batch');
    } else {
      setFileBlob(files[0]);
      setFilePreview(URL.createObjectURL(files[0]));
      setFileName(files[0].name);
      setStep('details');
    }
  };

  const capturePhoto = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      setCameraStream(stream);
      setStep('camera');
      setTimeout(() => { if (videoPreviewRef.current) videoPreviewRef.current.srcObject = stream; }, 100);
    } catch (err) {
      setError('Camera access denied. Please allow camera permissions.');
    }
  };

  const takePhoto = () => {
    if (!videoPreviewRef.current) return;
    const video = videoPreviewRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
    if (cameraStream) { cameraStream.getTracks().forEach((t) => t.stop()); setCameraStream(null); }
    setImageSrc(dataUrl);
    setStep('crop');
  };

  const startRecording = async (type) => {
    try {
      const constraints = type === 'video'
        ? { video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: true }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setCameraStream(stream);
      setStep('recording');
      if (type === 'video') {
        setTimeout(() => { if (videoPreviewRef.current) videoPreviewRef.current.srcObject = stream; }, 100);
      }
      chunksRef.current = [];
      const mimeType = type === 'video' ? 'video/webm' : 'audio/webm';
      const options = type === 'video' ? { mimeType, videoBitsPerSecond: 2500000 } : { mimeType };
      const recorder = new MediaRecorder(stream, options);
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setFileBlob(blob);
        setFilePreview(URL.createObjectURL(blob));
        setFileName(`recorded-${type}.webm`);
        stream.getTracks().forEach((t) => t.stop());
        setCameraStream(null);
        setStep('details');
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch (err) {
      setError(type === 'video' ? 'Camera/mic access denied.' : 'Microphone access denied.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const handleCropConfirm = async () => {
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      setFileBlob(blob);
      setCroppedPreview(URL.createObjectURL(blob));
      setStep('details');
    } catch (err) {
      setError('Failed to crop image');
    }
  };

  const handleBack = () => {
    if (step === 'crop' || step === 'camera' || step === 'recording') {
      resetState();
    } else if (step === 'batch') {
      setFileQueue([]);
      resetState();
    } else if (step === 'details') {
      if (mediaType === 'image' && imageSrc) { setStep('crop'); setCroppedPreview(null); setFileBlob(null); }
      else resetState();
    }
  };

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const activeTab = MEDIA_TABS.find((t) => t.key === mediaType);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4" onClick={onClose} data-testid="upload-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()}
        className="rounded-2xl max-w-lg w-full max-h-[92vh] overflow-hidden flex flex-col border border-[#D4AF37]/15"
        style={{ background: 'linear-gradient(180deg, #0c0c14 0%, #05050A 100%)' }}>

        {/* Header */}
        <div className="flex justify-between items-center px-5 py-3 border-b border-[#D4AF37]/10">
          <div className="flex items-center gap-3">
            {step !== 'select' && (
              <button onClick={handleBack} className="text-white/40 hover:text-[#D4AF37] text-sm font-['Outfit'] font-medium" data-testid="crop-back-button">Back</button>
            )}
            <h3 className="font-['Cormorant_Garamond'] text-lg font-light text-white capitalize">
              {step === 'select' && `Add ${category} Vibe`}
              {step === 'crop' && 'Crop Photo'}
              {step === 'camera' && 'Take Photo'}
              {step === 'recording' && (mediaType === 'video' ? 'Record Video' : 'Record Audio')}
              {step === 'batch' && `Upload ${fileQueue.length} Vibes`}
              {step === 'details' && 'Add Details'}
            </h3>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white" data-testid="close-upload-modal"><X size={22} /></button>
        </div>

        {error && <div className="bg-red-50 border-b border-red-200 text-red-700 px-5 py-2 font-body text-sm" data-testid="upload-error">{error}</div>}

        {step === 'select' && (
          <MediaSelector
            mediaType={mediaType}
            onMediaTypeChange={(type) => { resetState(); setMediaType(type); }}
            onFileChange={onFileChange}
            onCapturePhoto={capturePhoto}
            onStartVideoRecording={() => startRecording('video')}
            onStartAudioRecording={() => startRecording('audio')}
          />
        )}

        {step === 'camera' && (
          <CameraStep videoPreviewRef={videoPreviewRef} onTakePhoto={takePhoto} accentColor={activeTab.color} />
        )}

        {step === 'recording' && (
          <RecordingStep
            mediaType={mediaType} videoPreviewRef={videoPreviewRef}
            isRecording={isRecording} recordingTime={recordingTime}
            formatTime={formatTime} onStop={stopRecording} accentColor={activeTab.color}
          />
        )}

        {step === 'crop' && imageSrc && (
          <CropControls
            imageSrc={imageSrc} crop={crop} zoom={zoom} rotation={rotation} aspect={aspect}
            onCropChange={setCrop} onZoomChange={setZoom}
            onRotationChange={setRotation} onCropComplete={onCropComplete}
            onAspectChange={setAspect} onConfirm={handleCropConfirm} accentColor={activeTab.color}
          />
        )}

        {step === 'batch' && (
          <BatchStep
            fileQueue={fileQueue} setFileQueue={setFileQueue}
            note={note} onNoteChange={setNote} vibeDate={vibeDate} onDateChange={setVibeDate}
            uploading={uploading} uploadProgress={uploadProgress} uploadPercent={uploadPercent}
            onSubmit={() => uploadBatch({ fileQueue, note, vibeDate })} accentColor={activeTab.color}
            category={category}
            onHunterFinished={handleHunterFinished}
          />
        )}

        {step === 'details' && (
          <DetailsStep
            mediaType={mediaType} croppedPreview={croppedPreview} filePreview={filePreview} fileName={fileName}
            note={note} onNoteChange={setNote} vibeDate={vibeDate} onDateChange={setVibeDate}
            uploading={uploading} uploadPercent={uploadPercent} fileBlob={fileBlob}
            onSubmit={() => uploadSingle({ fileBlob, fileName, mediaType, note, vibeDate })} accentColor={activeTab.color}
            category={category}
            onHunterFinished={handleHunterFinished}
          />
        )}
      </motion.div>

      {/* Post-upload celebration overlay */}
      <VibeCelebration
        category={category}
        visible={showCelebration}
        onComplete={handleCelebrationComplete}
        uploadedPreview={croppedPreview || filePreview || (fileQueue[0] ? URL.createObjectURL(fileQueue[0]) : null)}
      />
    </div>
  );
};
